'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatNumber } from '@/utils/coins/formatNumber';
import { LoadingSpinner } from '@/components/Icons';
import { HistoryItem } from '@/types/coins/history';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useCoinPriceSocket } from '@/hooks/useCoinPriceSocket';
import { CHART_RANGE_SECONDS, isChartRange, pointsInRange } from '@/utils/coins/chartRanges';
import { CLOSED_RANGE_KEYS, ClosedRange, closedHoursLabel } from '@/utils/coins/marketClosed';
import PriceAreaChart, {
    ChartPoint,
} from '@/components/(postTypes)/symbols/single/coin/PriceAreaChart';

interface SingleCoinLineProps {
    data: HistoryItem[] | null;
    timePeriod: string;
    type: number;
    displayName?: string;
    symbol?: string;
    symbolType?: 'crypto' | 'gold' | 'currency';
    hideCurrencySelector?: boolean;
    changes?: Record<string, number>;
    /** `data` is still the previous range's (the selected one is loading) */
    loading?: boolean;
    /** "USD" when a gold/currency series is in dollars (the world gold ounce), toman otherwise */
    quoteCurrency?: string;
    /** Market-closed hours from the history API (`market_closed_ranges`) */
    closedRanges?: ClosedRange[];
}

type CurrencyKey = 'price' | 'price_in_toman';

const fmtPrice = (v: number) => formatNumber(v, false, true, 'USD', true);

const lastDataLabel = (ts: number) => {
    try {
        return new Date(ts * 1000).toLocaleString('fa-IR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch {
        return '';
    }
};

const SingleCoinLine: React.FC<SingleCoinLineProps> = ({
    data,
    timePeriod,
    type,
    symbol,
    symbolType,
    hideCurrencySelector = false,
    loading = false,
    quoteCurrency,
    closedRanges,
}) => {
    // A dollar series (the world gold ounce) is charted and labelled in dollars, and its live
    // ticks come from the socket's price_usd, never from the toman price
    const usdSeries = (quoteCurrency || '').toUpperCase() === 'USD';
    const kind = symbolType || (type === 2 ? 'gold' : type === 3 ? 'currency' : 'crypto');
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyKey>('price');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [viewport, setViewport] = useState({ wide: true, height: 800 });
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        setSelectedCurrency(type === 2 && !usdSeries ? 'price_in_toman' : 'price');
    }, [type, usdSeries]);

    // Chart height follows the screen (and fills it in fullscreen)
    useEffect(() => {
        const update = () =>
            setViewport({ wide: window.innerWidth >= 640, height: window.innerHeight });
        update();
        window.addEventListener('resize', update);
        const onFs = () => setIsFullscreen(document.fullscreenElement === wrapperRef.current);
        document.addEventListener('fullscreenchange', onFs);
        return () => {
            window.removeEventListener('resize', update);
            document.removeEventListener('fullscreenchange', onFs);
        };
    }, []);

    const toggleFullscreen = () => {
        if (!wrapperRef.current) return;
        if (!document.fullscreenElement) wrapperRef.current.requestFullscreen().catch(() => {});
        else document.exitFullscreen().catch(() => {});
    };

    // Live price from the socket, added as the newest point
    const { livePriceToman, livePriceUsdt } = useCoinPriceSocket(symbol || '', 0, 0, kind);

    const rangePoints = useMemo(
        () => pointsInRange(data, timePeriod, (item) => item[selectedCurrency]),
        [data, timePeriod, selectedCurrency],
    );

    const newestTs = rangePoints.length ? rangePoints[rangePoints.length - 1].timestamp : 0;
    const rangeSeconds = isChartRange(timePeriod) ? CHART_RANGE_SECONDS[timePeriod] : 86400;

    // Live prices since this history loaded: a new point every ~1/300 of the range, the newest one
    // follows the price until then. New history (another range, a reload) starts a new tail.
    const [tail, setTail] = useState<{ timestamp: number; usd: number; toman: number }[]>([]);
    const dataVersion = useRef({ data, n: 0 });
    if (dataVersion.current.data !== data)
        dataVersion.current = { data, n: dataVersion.current.n + 1 };
    useEffect(() => setTail([]), [data]);
    useEffect(() => {
        if (!(livePriceToman > 0) && !(livePriceUsdt > 0)) return;
        const step = Math.max(15, rangeSeconds / 300);
        const now = Math.floor(Date.now() / 1000);
        setTail((t) => {
            const p = { timestamp: now, usd: livePriceUsdt, toman: livePriceToman };
            const last = t[t.length - 1];
            const anchor = t[t.length - 2];
            // The newest point moves with the price until a whole step has passed since the one before it
            if (last && anchor && now - anchor.timestamp < step) return [...t.slice(0, -1), p];
            return [...t, p].slice(-400);
        });
    }, [livePriceToman, livePriceUsdt, rangeSeconds]);

    // Feed stopped for this range → say when the data ends (and don't glue today's price onto it)
    const staleSince = useMemo(() => {
        if (rangePoints.length < 2) return null;
        const span = isChartRange(timePeriod) ? CHART_RANGE_SECONDS[timePeriod] : 86400;
        const allowedLag = Math.max(span / 4, 3 * 3600);
        return Date.now() / 1000 - newestTs > allowedLag ? newestTs : null;
    }, [rangePoints, newestTs, timePeriod]);

    const isUsdView = (kind === 'crypto' || usdSeries) && selectedCurrency === 'price';
    const unit = isUsdView ? '$' : 'تومان';

    const chartPoints: ChartPoint[] = useMemo(() => {
        // Crypto shows the other currency in the info card; gold/currency prices are toman only
        const extraOf = (usd: number, toman: number) => {
            if (kind !== 'crypto') return undefined;
            if (isUsdView)
                return toman > 0
                    ? [{ label: 'قیمت تومانی', value: `${fmtPrice(toman)} تومان` }]
                    : [];
            return usd > 0 ? [{ label: 'قیمت دلاری', value: `$${fmtPrice(usd)}` }] : [];
        };

        const pts: ChartPoint[] = rangePoints.map((p) => ({
            timestamp: p.timestamp,
            value: p.value,
            extra: extraOf(Number(p.item.price) || 0, Number(p.item.price_in_toman) || 0),
        }));

        if (pts.length >= 2 && !staleSince) {
            for (const t of tail) {
                const val = selectedCurrency === 'price_in_toman' ? t.toman : t.usd;
                const last = pts[pts.length - 1];
                if (!(val > 0) || t.timestamp <= last.timestamp) continue;
                pts.push({ timestamp: t.timestamp, value: val, extra: extraOf(t.usd, t.toman) });
            }
            // The window slides forward with the live points
            const from = pts[pts.length - 1].timestamp - rangeSeconds;
            while (pts.length > 2 && pts[0].timestamp < from) pts.shift();
        }
        return pts;
    }, [rangePoints, kind, isUsdView, selectedCurrency, tail, staleSince, rangeSeconds]);

    // Closed hours apply to toman prices (Iranian market); dollar crypto prices trade around the clock
    const closedInView = useMemo(() => {
        if (isUsdView || !closedRanges?.length || !CLOSED_RANGE_KEYS.includes(timePeriod))
            return [];
        if (chartPoints.length < 2) return [];
        const t0 = chartPoints[0].timestamp;
        const t1 = chartPoints[chartPoints.length - 1].timestamp;
        // Ranges that cover at least an hour of the chart, kept whole (the chart clips them itself)
        return closedRanges.filter((r) => Math.min(r.to, t1) - Math.max(r.from, t0) >= 3600);
    }, [isUsdView, closedRanges, timePeriod, chartPoints]);

    const chartHeight = isFullscreen
        ? Math.max(300, viewport.height - 140)
        : viewport.wide
          ? 380
          : 260;
    const isEmpty = !loading && chartPoints.length < 2;

    return (
        <div
            ref={wrapperRef}
            className={`relative w-full flex flex-col gap-2 ${isFullscreen ? 'bg-white dark:bg-slate-900 p-4 overflow-y-auto' : ''}`}
        >
            {/* LTR row: currency selector left, tools beside it (the page's tabs float over the right end) */}
            <div dir='ltr' className='flex h-7 items-center gap-2 z-10 relative mb-1 px-1'>
                {!hideCurrencySelector && (
                    <div className='bg-gray-100 dark:bg-white/10 p-0.5 rounded-lg flex gap-0.5'>
                        {(
                            [
                                ['price', 'دلاری'],
                                ['price_in_toman', 'تومانی'],
                            ] as const
                        ).map(([key, label]) => (
                            <button
                                key={key}
                                type='button'
                                onClick={() => setSelectedCurrency(key)}
                                className={`rounded-md px-3 py-1 text-center text-xs font-medium transition-all cursor-pointer ${
                                    selectedCurrency === key
                                        ? 'bg-white dark:bg-blue-600 text-blue-600 dark:text-white shadow-sm font-bold'
                                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                <button
                    type='button'
                    onClick={toggleFullscreen}
                    title={isFullscreen ? 'خروج از حالت تمام صفحه' : 'تمام صفحه'}
                    aria-label={isFullscreen ? 'خروج از حالت تمام صفحه' : 'تمام صفحه'}
                    className='p-1.5 rounded-lg bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-white transition-all cursor-pointer'
                >
                    {isFullscreen ? (
                        <Minimize2 className='size-3.5' />
                    ) : (
                        <Maximize2 className='size-3.5' />
                    )}
                </button>
            </div>

            <div className='relative w-full' style={{ minHeight: chartHeight }}>
                {loading && (
                    <div className='absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 z-30 backdrop-blur-[2px]'>
                        <div
                            className='flex items-center gap-2.5 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl px-4 py-2.5 shadow-lg'
                            dir='rtl'
                        >
                            <LoadingSpinner className='size-5 animate-spin text-blue-600 dark:text-blue-400' />
                            <span className='text-xs font-medium text-slate-700 dark:text-slate-200'>
                                در حال به‌روزرسانی نمودار...
                            </span>
                        </div>
                    </div>
                )}

                {isEmpty ? (
                    <div
                        className='flex items-center justify-center rounded-xl bg-gray-50 dark:bg-white/5 text-xs text-gray-500 dark:text-gray-400'
                        style={{ height: chartHeight }}
                        dir='rtl'
                    >
                        برای این بازهٔ زمانی داده‌ای ثبت نشده است.
                    </div>
                ) : (
                    <PriceAreaChart
                        points={chartPoints}
                        range={timePeriod}
                        formatValue={fmtPrice}
                        unit={unit}
                        height={chartHeight}
                        closedRanges={closedInView}
                        live={!staleSince && tail.length > 0}
                        animateKey={`${timePeriod}|${selectedCurrency}|${dataVersion.current.n}`}
                    />
                )}
            </div>

            {closedInView.length > 0 && !loading && (
                <p className='text-[11px] text-gray-500 dark:text-gray-400 px-1' dir='rtl'>
                    نوار «بسته»: ساعت‌های توقف معاملات ({closedHoursLabel(closedInView)}) که از
                    نمودار حذف شده‌اند.
                </p>
            )}

            {staleSince && !loading && (
                <p className='text-[11px] text-amber-600 dark:text-amber-400 px-1' dir='rtl'>
                    آخرین داده در این بازه: {lastDataLabel(staleSince)}
                </p>
            )}
        </div>
    );
};

export default SingleCoinLine;
